import { Controller, useFormContext } from "react-hook-form";
import { useState, useEffect, useRef } from "react";
import CustomTextInput from "../components/CustomTextInput";
import CustomSelect from "../components/CustomSelect";
import { Button } from "flowbite-react";
import {
  useGetPrioritiesQuery,
  useGetCategoriesQuery,
  useGetClientsQuery,
  useGetAgentsQuery,
} from "../api/ticketApi/selectInputApis";
import { Ticket } from "@/sharedTypes";
import SearchWithDropdown from "./SearchWithDropdown";

interface TicketFormFilterProps {
  filter: Ticket;
  onFilterApply: (filters: Ticket) => void;
  onCancel: () => void;
  showInactive: boolean;
  setShowInactive: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function TicketFormFilter({
  filter,
  onFilterApply,
  showInactive,
  setShowInactive,
}: TicketFormFilterProps) {
  const { control, handleSubmit, reset, register } = useFormContext<any>();

  const [clientSearchRecord, setClientSearchRecord] = useState<Record<string, any>>(
    {}
  );

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data: priorities } = useGetPrioritiesQuery();
  const { data: categories } = useGetCategoriesQuery();
  const { data: clients } = useGetClientsQuery(clientSearchRecord);
  const { data: agents } = useGetAgentsQuery();

  const [clientOptions, setClientOptions] = useState(
    clients?.items?.map((client: any) => ({
      value: client.client_id?.toString(),
      label: client.client_username,
    })) || []
  );

  useEffect(() => {
    setClientOptions(
      clients?.items?.map((client: any) => ({
        value: client.client_id?.toString(),
        label: client.client_username,
      })) || []
    );
  }, [clients]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleClientSearch = (query: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setClientSearchRecord((prev) => ({
        ...prev,
        client_username: query,
        client_email: query,
      }));
    }, 500);
  };

  const onSubmit = (data: Ticket) => {
    const filters = { ...data, is_active: showInactive ? undefined : false };
    onFilterApply(filters);
  };

  const handleReset = () => {
    reset();
    onFilterApply({
      ...filter,
      is_active: showInactive ? undefined : false,
    });
  };

  const channels = [
    { value: "0", label: "Platform" },
    { value: "1", label: "Mail" },
    { value: "2", label: "Widget" },
  ];

  const statuses = [
    { value: "1", label: "Open" },
    { value: "2", label: "Pending" },
    { value: "3", label: "Resolved" },
  ];

  return (
    <div className="max-h-50 overflow-y-auto p-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => {
              reset();
              setShowInactive((prev) => !prev);
            }}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              showInactive
                ? "bg-purple-500 text-white hover:bg-purple-600"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {showInactive ? "Hide Inactive Tickets" : "Show Inactive Tickets"}
          </button>
        </div>

        <CustomTextInput
          label="Ticket Title"
          placeholder="Enter ticket title"
          {...register("ticket_title")}
        />

        <Controller
          name="last_category_id"
          control={control}
          render={({ field }) => (
            <CustomSelect
              label="Category"
              options={categories?.items?.map((category: any) => ({
                value: category.category_id?.toString(),
                label: category.category_name,
              }))}
              value={field.value?.toString() || ""}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v ? parseInt(v) : undefined);
              }}
            />
          )}
        />

        <Controller
          name="channel"
          control={control}
          render={({ field }) => (
            <CustomSelect
              label="Channel"
              options={channels}
              value={field.value?.toString() || ""}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v ? parseInt(v) : undefined);
              }}
            />
          )}
        />

        <Controller
          name="last_status_id"
          control={control}
          render={({ field }) => (
            <CustomSelect
              label="Status"
              options={statuses}
              value={field.value?.toString() || ""}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v ? parseInt(v) : undefined);
              }}
            />
          )}
        />

        <Controller
          name="client_id"
          control={control}
          render={({ field }) => (
            <SearchWithDropdown
              label="Client"
              options={clientOptions}
              placeholder="Search or select a client"
              value={field.value || ""}
              onSearch={handleClientSearch}
              onChange={(selectedOption) =>
                field.onChange(selectedOption?.value || null)
              }
            />
          )}
        />

        <Controller
          name="last_priority_id"
          control={control}
          render={({ field }) => (
            <CustomSelect
              label="Priority"
              options={priorities?.items?.map((priority: any) => ({
                value: priority.priority_id?.toString(),
                label: priority.priority_name,
              }))}
              value={field.value?.toString() || ""}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v ? parseInt(v) : undefined);
              }}
            />
          )}
        />

        <Controller
          name="last_assigned_agent_id"
          control={control}
          render={({ field }) => (
            <CustomSelect
              label="Assigned Agent"
              options={agents?.items?.map((agent: any) => ({
                value: agent.agent_id?.toString(),
                label: agent.agent_username,
              }))}
              value={field.value?.toString() || ""}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v ? parseInt(v) : undefined);
              }}
            />
          )}
        />

        <div className="flex justify-end space-x-2">
          <Button type="button" color="failure" onClick={handleReset}>
            Reset
          </Button>
          <Button
            type="submit"
            className="bg-gradient-to-r from-green-400 to-green-600 text-white"
          >
            Apply
          </Button>
        </div>
      </form>
    </div>
  );
}
